import { useEffect, useState } from 'react';
import SimpleDropdown from '../form-inputs/simpleDropdown';
import PostChangeUserRole from '../../services/adapters/changeUserRole';
import useToastStore from '../app-layout/toastStore';
import useRequestState from '../../hooks/useRequestState';
import GetAssignableRoles from '../../services/adapters/assignableRoles';
import type { OptionType } from '../../types/forms.type';
import useAuth from '../../utils/auth';
import Role from '../../types/enums';
import { DisplayOrganisationUser } from '../../types/core.type';

export default function RoleCell({
  onRoleChanged,
  user,
}: {
  user: DisplayOrganisationUser;
  onRoleChanged: (email: string, newRole: Role) => void;
}) {
  const [isUpdating, setIsUpdating] = useState(false);
  const currentUser = useAuth();
  const {
    data: roleOptions,
    handleRequest: fetchAssignableRoles,
  } = useRequestState<OptionType[]>();

  const assignableRoles = roleOptions?.map((role) => role.value) || [];

  useEffect(() => {
    void fetchAssignableRoles(new GetAssignableRoles().adaptResponse());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.role]);
  if (roleOptions?.length === 0 || currentUser.role === user.role || !assignableRoles.includes(user.role)) {
    return <span>{user.role}</span>;
  }

  const handleChange = async (value: string) => {
    const newRole = value as Role;
    if (newRole === user.role) return;

    setIsUpdating(true);
    try {
      const adapter = new PostChangeUserRole();
      await adapter.adaptResponse({ targetUserEmail: user.email, newRoleName: newRole });
      onRoleChanged(user.email, newRole);
    } catch (error) {
      useToastStore.getState().showToast(error instanceof Error ? error.message : 'Failed to update role', 'error');
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <SimpleDropdown
      id={`role-${user.email}`}
      options={roleOptions || []}
      selected={isUpdating ? 'Updating...' : user.role}
      onChange={handleChange}
    />
  );
}
